import React, { useEffect, useState } from 'react';

import {
  Button,
  Card,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';
import { Close, Group } from '@material-ui/icons';
import { ConfirmDialog } from '@sierralabs/react-material-ui';

import AppSnackbar from '../../../common/AppSnackbar';
import CardTitle from '../../../common/card/CardTitle';
import { roles as roleLabels } from '../../../user/User.interface';
import { Role } from '../../roles/Role.interface';
import GroupRoleAssignDialog from '../dialogs/GroupRoleAssignDialog';
import { useFindGroupRoles, useRemoveRolesFromGroup } from '../Group.hooks';

export interface GroupRolesCardProps {
  groupId: number;
  onUpdate?: () => void;
}

const GroupRolesCard: React.FC<GroupRolesCardProps> = ({
  groupId,
  onUpdate
}) => {
  const [assignDialog, setAssignDialog] = useState(false);
  const [unassignconfirmDialog, setUnassignConfirmDialog] = useState(false);
  const [updateUserconfirmDialog, setUpdateUserConfirmDialog] = useState(false);
  const [unassign, setUnassign] = useState<Role>();

  // Load or refreshing existing references
  const {
    groupRoles,
    loadingGroupRoles,
    errorLoadingGroupRoles,
    findGroupRoles
  } = useFindGroupRoles(groupId);
  useEffect(() => findGroupRoles(), [findGroupRoles]);
  const [roles, setRoles] = useState(groupRoles);
  useEffect(() => void (groupRoles && setRoles(groupRoles)), [groupRoles]);

  // Removing a role
  const {
    updatedGroupRoles,
    updatingGroupRoles,
    errorUpdatingGroupRoles,
    removeUsersFromGroup
  } = useRemoveRolesFromGroup(groupId);
  useEffect(() => {
    if (updatedGroupRoles) {
      setRoles(updatedGroupRoles);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedGroupRoles]);

  return (
    <Card>
      <CardTitle>
        <Group />
        Team Roles
      </CardTitle>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(loadingGroupRoles || updatingGroupRoles) && (
              <TableRow>
                <TableCell colSpan={2} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {roles?.map(role => (
              <TableRow key={role.id}>
                <TableCell>
                  {roleLabels.find(r => r.name === role.name)?.label ||
                    role.name}
                </TableCell>
                <TableCell style={{ textAlign: 'center' }}>
                  <IconButton
                    size='small'
                    onClick={() => {
                      setUnassignConfirmDialog(true);
                      setUnassign(role);
                    }}
                  >
                    <Close />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={2}>
                <Button
                  variant='outlined'
                  color='primary'
                  onClick={() => setAssignDialog(true)}
                >
                  Assign Roles
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {assignDialog && roles && (
        <GroupRoleAssignDialog
          groupId={groupId}
          roles={roles}
          onCancel={() => {
            setAssignDialog(false);
          }}
          onSubmit={() => {
            setAssignDialog(false);
            findGroupRoles();
            onUpdate?.();
          }}
        />
      )}
      {unassignconfirmDialog && (
        <ConfirmDialog
          open
          title='Are you sure?'
          message='Are you sure you want to un-assign this role?'
          onCancel={() => {
            setUnassignConfirmDialog(false);
            findGroupRoles();
          }}
          onConfirm={() => {
            setUnassignConfirmDialog(false);
            setUpdateUserConfirmDialog(true);
          }}
        />
      )}
      {updateUserconfirmDialog && unassign && (
        <ConfirmDialog
          open
          title='Update User Roles'
          message='Would you like to update all user roles with these new settings?'
          cancelLabel='No'
          confirmLabel='Yes'
          onCancel={() => {
            if (unassign) {
              removeUsersFromGroup(unassign.id, false);
            }
            setUnassign(undefined);
            setUpdateUserConfirmDialog(false);
          }}
          onConfirm={() => {
            if (unassign) {
              removeUsersFromGroup(unassign.id, true);
            }
            setUnassign(undefined);
            setUpdateUserConfirmDialog(false);
          }}
        />
      )}
      {errorLoadingGroupRoles && (
        <AppSnackbar open severity='error'>
          Error getting roles. Please check your internet connection and try
          again.
        </AppSnackbar>
      )}
      {errorUpdatingGroupRoles && (
        <AppSnackbar open severity='error'>
          Error removing role.
        </AppSnackbar>
      )}
    </Card>
  );
};

export default GroupRolesCard;
